import React from 'react';
import PropTypes from 'prop-types';
import shaka from 'shaka-player';
import Spinner from 'react-spinkit';
import './videoplayer.scss';
import DebugOverlay from '../DebugOverlay';
import { getLogsFlag, setLogsFlag, getLogoFlag, fireAnalytics } from '../../pages/utils'

export class VideoPlayer extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      browserSupported: shaka.Player.isBrowserSupported(),
      hvcSupported: false,
      manifestLoaded: false,
      videoPlaying: false,
      variantTracks: null,
      streamStats: null,
      buffering: true,
      debugInterval: null,
      artistName: props.artistName,
      showLogo: props.showLogo,
      subscription: props.subscription,
    };
    this.videoElement = React.createRef();
    this.player = null;
    this.url = props.videoUrl;
  }

  async componentDidMount() {
    const { videoUrl } = this.props;
    if (videoUrl !== null && !this.player) this.initializePlayer();
    if (this.props.showDebug) {
      if (getLogsFlag() === true)
        setLogsFlag(this.props.showDebug);
      else if (getLogsFlag() === "")
        setLogsFlag(this.props.showDebug);
    }
    console.log("Subscription : ", this.state.subscription);
    if (this.state.subscription) {
      if (getLogoFlag()) this.setState({ showLogo: true })
      setInterval(() => {
        if (getLogoFlag()) this.setState({ showLogo: true })
        else this.setState({ showLogo: false })
      }, 5000);
    }
  }

  async componentWillReceiveProps(nextProps) {
    const { debugInterval } = this.state;
    this.setState(() => ({ artistName: nextProps.artistName }));
    if (nextProps.videoUrl !== this.url) {
      if (debugInterval) {
        clearInterval(debugInterval);
        this.setState({ debugInterval: null });
      }
      this.setState(() => ({ videoPlaying: false }));
      await this.player.unload();
      this.setState({ manifestLoaded: false });
      await this.player.destroy();
      this.player = null;
      this.url = nextProps.videoUrl;
      await this.initializePlayer();
    }
  }

  async initializePlayer() {
    const origIsTypeSupported = MediaSource.isTypeSupported;
    this.player = new shaka.Player(this.videoElement.current);
    const isHvcSupported = origIsTypeSupported.call(MediaSource, 'video/mp4;codecs="hvc1.1.6.L153.B0"');
    this.setState({ hvcSupported: isHvcSupported });
    MediaSource.isTypeSupported = (type) => {
      if (isHvcSupported && /codecs="avc/.exec(type)) {
        return false;
      }
      return origIsTypeSupported.call(MediaSource, type);
    };
    // this.player.setMaxHardwareResolution(window.screen.width * window.devicePixelRatio, window.screen.height * window.devicePixelRatio);
    this.player.addEventListener('buffering', (e) => {
      this.setState(() => ({ buffering: e.buffering }));
    });
    if (this.player) {
      this.loadManifest();
    }
  }

  async teardownPlayer() {
    const { debugInterval } = this.state;
    if (debugInterval) {
      clearInterval(debugInterval);
      this.setState({ debugInterval: null });
    }

    await this.videoElement.current?.pause();
    this.setState(() => ({ videoPlaying: false }));
    await this.player.unload();
    this.setState({ manifestLoaded: false });
    this.player.destroy();
    this.player = null;
  }

  async loadManifest() {
    try {
      await this.player.load(this.url);
      this.player.configure('abr.defaultBandwidthEstimate', 12000000);
      this.setState(() => ({ manifestLoaded: true }));
      if (this.state.manifestLoaded && this.props.autoplay) this.playVideo();
      if (this.state.manifestLoaded && this.props.autoplay && this.state.buffering) this.loadDebugData();
    } catch (e) {
      console.log(e);
    }
  }

  async playVideo() {
    try {
      await this.player.configure('abr.defaultBandwidthEstimate', 12000000);
      let bufferBehindVal = 72;
      if (!this.state.hvcSupported) bufferBehindVal = 36;
      await this.player.configure({
        streaming: {
          bufferingGoal: 12,
          bufferBehind: bufferBehindVal
        }
      });
      await this.videoElement.current.play();
      setTimeout(() => {
        this.setState({ artistName: false });
      }, 5000);
      this.setState(() => ({ videoPlaying: true }));
      fireAnalytics('PlayVideo', 'Play', this.url);
    } catch (e) {
      console.log(e);
    }
  }

  async loadDebugData() {
    if (this.player) {
      const interval = setInterval(() => {
        this.setState(() => ({ streamStats: this.player.getStats() }));
        this.setState(() => ({ variantTracks: this.player.getVariantTracks() }));
      }, 100);
      this.setState(() => ({ debugInterval: interval }));
    }
  }

  render() {
    return (
      <div className="Wrapper">
        {(!this.state.manifestLoaded || this.state.buffering) && (
          <div className="Loader">
            <Spinner className="VideoLoader" name="double-bounce" color="white" fadeIn="none" style={{ width: '70px', height: '70px' }} />
          </div>
        )}
        <DebugOverlay
          visible={this.props.showDebug}
          browserSupported={this.state.browserSupported}
          hvcSupported={this.state.hvcSupported}
          shakaPlayer={this.player}
          manifestLoaded={this.state.manifestLoaded}
          videoPlaying={this.state.videoPlaying}
          streamStats={this.state.streamStats}
          variantTracks={this.state.variantTracks}
          buffering={this.state.buffering}
          muted={this.props.muted}
          totalTime={this.videoElement.current?.duration}
          currentTime={this.videoElement.current?.currentTime}
        />
        <img className="poster" src={this.props.posterUrl} style={!this.state.videoPlaying ? { opacity: '1' } : { opacity: '0' }} />
        {
          this.state.showLogo &&
          <div className="video-artistName">
            <div className="ArtworkName">{this.props.artworkName}</div>
            <div className="ArtistName">{this.state.artistName}</div>
          </div>
        }
        {this.state.showLogo && <img className="blackdovelogo" src="https://assets.blackdove.com/images/blackdove-logo-2020.png" alt="" />}
        <video ref={this.videoElement} loop={this.props.loop} muted={this.props.muted} preload="metadata" />
      </div>
    );
  }
}

VideoPlayer.propTypes = {
  artistName: PropTypes.string,
  autoplay: PropTypes.bool,
  loop: PropTypes.bool,
  muted: PropTypes.bool,
  showDebug: PropTypes.bool,
  showLogo: PropTypes.bool,
};

VideoPlayer.defaultProps = {
  artistName: null,
  autoplay: true,
  loop: true,
  muted: false,
  showDebug: false,
  showLogo: false,
};

export default VideoPlayer;
